import { logger } from './logger';
import {
  LocalTrackPublication,
  Participant,
  RemoteParticipant,
  Room,
  RoomEvent,
  RoomOptions,
  Track,
  TrackPublication,
} from 'livekit-client';

import {
  MediaProvider,
  MediaProviderSid,
  TrackType,
} from '@withso/pop-media-sdk';

const liveKitSourceMap = {
  [TrackType.Camera]: Track.Source.Camera,
  [TrackType.Microphone]: Track.Source.Microphone,
  [TrackType.Screen]: Track.Source.ScreenShare,
  [TrackType.ScreenAudio]: Track.Source.ScreenShareAudio,
};
const trackTypeMap = {
  [Track.Source.Camera]: TrackType.Camera,
  [Track.Source.Microphone]: TrackType.Microphone,
  [Track.Source.ScreenShare]: TrackType.Screen,
  [Track.Source.ScreenShareAudio]: TrackType.ScreenAudio,
};

export class LiveKitProvider extends MediaProvider {
  private _room: Room | null = null;
  private _identityMap = new Map<string, string>();

  constructor(private endpoint: string, private roomOptions: RoomOptions = {}) {
    super();
    console.log('[LiveKit] Provider initialized with endpoint:', endpoint);
  }

  protected connectToRoom = async (
    mediaToken: string,
    localTracks: Record<TrackType, MediaStreamTrack | null>,
  ) => {
    console.log('[LiveKit] connectToRoom called with token length:', mediaToken?.length);
    const room = new Room({
      ...this.roomOptions,
      // this is necessary to enforce to maintain SDK behavior consistency
      stopLocalTrackOnUnpublish: false,
    });
    this._room = room;

    logger.debug(
      '[LiveKit] connected, local participant SID',
      room.localParticipant.sid,
    );

    room.on(RoomEvent.Disconnected, this.handleDisconnect);
    room.on(RoomEvent.Reconnected, this.handleReconnect);
    room.on(RoomEvent.Reconnecting, this.handleReconnecting);
    room.on(RoomEvent.ParticipantConnected, this.handleParticipantConnected);
    room.on(
      RoomEvent.ParticipantDisconnected,
      this.handleParticipantDisconnected,
    );
    room.on(RoomEvent.TrackSubscribed, this.handleTrackSubscribed);
    room.on(RoomEvent.TrackUnsubscribed, this.handleTrackUnsubscribed);
    room.on(RoomEvent.DataReceived, this.handleData);
    room.on(RoomEvent.LocalTrackPublished, this.handleLocalTrackPublished);
    room.on(RoomEvent.LocalTrackUnpublished, this.handleLocalTrackUnpublished);

    try {
      await room.connect(this.endpoint, mediaToken);
      console.log('[LiveKit] Room connected successfully!', room.name);
    } catch (err) {
      console.error('[LiveKit] Failed to connect to room:', err);
      throw err;
    }

    // immediately trigger connection events for all existing participants
    this.handleParticipantConnected(room.localParticipant);
    for (const participant of room.remoteParticipants.values()) {
      this.handleParticipantConnected(participant);
    }

    // immediately publish local tracks
    await Promise.all(
      Object.entries(localTracks).map(async ([trackType, track]) => {
        if (track) {
          console.log('[LiveKit] Publishing initial track:', trackType);
          await this.startMediaInternal(trackType as TrackType, track);
          logger.debug('[LiveKit] published initial local track', trackType);
        }
      }),
    );
  };

  private handleDisconnect = () => {
    logger.debug('[LiveKit] disconnected');
    this.onDisconnect();
  };

  private handleReconnecting = () => {
    logger.debug('[LiveKit] reconnecting');
    this.onReconnecting();
  };

  private handleReconnect = () => {
    logger.debug('[LiveKit] reconnected');
    this.onReconnect();
  };

  private handleParticipantConnected = (participant: Participant) => {
    console.log(`[LiveKit] participant connected: ${participant.identity} (${participant.sid})`);
    
    // Cache identity mapping reliably
    if (participant.sid && participant.identity) {
      this._identityMap.set(participant.sid, participant.identity);
    }

    // get the participant ID - it's stored as their identity
    const appId = participant.identity;
    this.onParticipantConnected(participant.sid, appId);
    // immediately trigger start events for all participant tracks
    for (const track of participant.trackPublications.values()) {
      if (track.track) {
        this.handleTrackSubscribed(track.track, track, participant);
      }
    }
  };

  private handleParticipantDisconnected = (participant: Participant) => {
    logger.debug(`[LiveKit] participant disconnected: ${participant.sid}`);
    this.onParticipantDisconnected(participant.sid);
    this._identityMap.delete(participant.sid);
    // immediately trigger end events for all participant tracks
    for (const track of participant.trackPublications.values()) {
      if (track.track) {
        this.handleTrackUnsubscribed(track.track, track, participant);
      }
    }
  };

  private getTrackType = (track: TrackPublication) => {
    if (track.source === Track.Source.ScreenShare && track.kind === 'audio') {
      return TrackType.ScreenAudio;
    }
    
    if (track.source === Track.Source.Unknown || !track.source) {
      // try the name...
      return track.trackName as TrackType;
    }
    return trackTypeMap[track.source];
  };

  private handleTrackSubscribed = (
    track: Track,
    pub: TrackPublication,
    participant: Participant,
  ) => {
    console.log(`[LiveKit] track subscribed: ${track.sid} from ${participant.identity} (${participant.sid}). Type: ${track.kind}`);
    /*
    logger.debug(
      `[LiveKit] track subscribed: ${track.sid} by ${participant.sid}. MediaStreamTrack ID: ${track.mediaStreamTrack.id}`,
    );
    */
    if (!track.sid) {
      // TODO: what?
      console.error('[LiveKit] track has no SID');
      // logger.error('[LiveKit] track has no SID');
      return;
    }
    const trackType = this.getTrackType(pub);
    console.log(`[LiveKit] Mapped track type: ${trackType}. Calling onTrackSubscribed for SID ${participant.sid}`);
    this.onTrackSubscribed(participant.sid, trackType);
  };

  private handleTrackUnsubscribed = (
    track: Track,
    pub: TrackPublication,
    participant: Participant,
  ) => {
    logger.debug(
      `[LiveKit] track unsubscribed: ${track.sid} by ${participant.sid}`,
    );
    if (!track.sid) {
      // TODO: what?
      logger.error('[LiveKit] track has no SID');
      return;
    }
    const trackType = this.getTrackType(pub);
    if (!trackType) {
      logger.warn(
        '[LiveKit] track unsubscribed but track type not recognized',
        track.sid,
        participant.sid,
      );
      return;
    }
    this.onTrackUnsubscribed(participant.sid, trackType);
  };

  protected disconnectFromRoom = async () => {
    if (this._room) {
      this._room.disconnect(false);
      // act as if our own participant disconnected - LiveKit will not fire these
      // events like it would for remote participants
      this.handleParticipantDisconnected(this._room.localParticipant);
      this._room = null;
    }
  };

  getMediaStreamTrack = (participantSid: string, trackSid: string) => {
    // console.log(`[LiveKit] getMediaStreamTrack called for ${participantSid} / ${trackSid}`);
    if (!this._room) return null;

    let participant: Participant | null;
    if (this._room.localParticipant.sid === participantSid || this._room.localParticipant.identity === participantSid) {
      participant = this._room.localParticipant;
    } else {
      participant = this.getParticipantBySid(participantSid);
    }

    if (!participant) {
      console.warn(`[LiveKit] Participant with sid/identity ${participantSid} not found`);
      return null;
    }

    // Try finding by SID first
    let mediaTrackPub = participant.trackPublications.get(trackSid);

    // If not found, try finding by TrackType (source/name)
    if (!mediaTrackPub) {
      // console.log(`[LiveKit] Track SID ${trackSid} not found in ${participant.trackPublications.size} pubs. Searching by type...`);
      for (const pub of participant.trackPublications.values()) {
        const type = this.getTrackType(pub);
        // console.log(`[LiveKit] Checking pub ${pub.trackSid} type: ${type}`);
        if (type === trackSid) {
          mediaTrackPub = pub;
          break;
        }
      }
    }

    if (!mediaTrackPub) {
      // console.warn(`[LiveKit] Media track with sid/type ${trackSid} not found for ${participant.identity}. Pubs available: ${participant.trackPublications.size}`);
      return null;
    }

    // const track = mediaTrackPub.track;
    // const stream = track?.mediaStreamTrack;
    // console.log(`[LiveKit] Found pub for ${trackSid}. Has track: ${!!track}, Has stream: ${!!stream}. Kind: ${track?.kind}`);
    
    return mediaTrackPub.track?.mediaStreamTrack ?? null;
  };

  protected getLocalParticipantSid = () => {
    return this._room?.localParticipant.sid ?? null;
  };

  getLocalParticipantIdentity = () => {
    return this._room?.localParticipant.identity ?? null;
  };

  protected publishLocalMedia = async (
    trackType: TrackType,
    mediaTrack: MediaStreamTrack,
  ) => {
    if (!this._room) {
      throw new Error('Cannot start media before room is connected');
    }

    logger.debug('[LiveKit] publishLocalMedia called', trackType, mediaTrack.id, mediaTrack.kind);

    try {
      const pub = await this._room.localParticipant.publishTrack(mediaTrack, {
        name: trackType,
        source: liveKitSourceMap[trackType],
      });
      logger.debug(
        '[LiveKit] published track success',
        pub.trackSid,
        liveKitSourceMap[trackType],
      );
      return pub.trackSid;
    } catch (error) {
      logger.error('[LiveKit] failed to publish track', trackType, error);
      throw error;
    }
  };

  protected unpublishLocalMedia = async (trackSid: string) => {
    if (!this._room) return;

    const pub = this._room.localParticipant.trackPublications.get(trackSid);
    if (!pub?.track) return;
    logger.debug('[LiveKit] unpublished track', pub.trackSid);
    this._room.localParticipant.unpublishTrack(pub.track);
  };

  private handleLocalTrackPublished = async (pub: LocalTrackPublication) => {
    const trackType = this.getTrackType(pub);
    if (this._room) {
      this.onLocalTrackPublished(trackType);
    }
  };
  private handleLocalTrackUnpublished = async (pub: LocalTrackPublication) => {
    const trackType = this.getTrackType(pub);
    if (this._room) {
      this.onLocalTrackUnpublished(trackType);
    }
  };

  private getParticipantBySid = (sid: string) => {
    if (!this._room) return null;

    if (this._room.localParticipant.sid === sid || this._room.localParticipant.identity === sid) {
      return this._room.localParticipant;
    }

    const remotePart = this._room.remoteParticipants.get(sid);
    if (remotePart) return remotePart;

    // Fallback: search by identity
    for (const p of this._room.remoteParticipants.values()) {
      if (p.identity === sid) {
        return p;
      }
    }
    return null;
  };

  protected getParticipantIdFromSid = (sid: MediaProviderSid) => {
    // Try reliable local map first
    let identity = this._identityMap.get(sid as string) ?? null;
    
    // Fallback to searching the room if missing
    if (!identity) {
       identity = this.getParticipantBySid(sid as string)?.identity ?? null;
    }

    console.log(`[LiveKit] getParticipantIdFromSid called for ${sid} -> ${identity}`);
    return identity;
  };

  protected getTrackTypeFromTrackSid = (
    participantSid: MediaProviderSid,
    trackSid: MediaProviderSid,
  ) => {
    if (!this._room) return null;

    const participant = this.getParticipantBySid(participantSid as string);
    const pub = participant?.trackPublications.get(trackSid as string);

    if (!pub?.track) return null;
    return this.getTrackType(pub);
  };

  protected getParticipantTrackSids = (participantSid: MediaProviderSid) => {
    if (!this._room) return [];

    const participant = this.getParticipantBySid(participantSid as string);
    if (!participant) return [];

    return Array.from(participant.trackPublications.keys());
  };

  private textEncoder = new TextEncoder();
  sendMessage(message: string): void {
    if (!this._room) return;
    this._room.localParticipant.publishData(
      this.textEncoder.encode(message),
      { reliable: false },
    );
  }

  private textDecoder = new TextDecoder('utf-8');
  private handleData = (
    message: Uint8Array,
    participant: RemoteParticipant | undefined,
  ) => {
    // If no participant (e.g. server-side data), use a placeholder or ignore based on app logic
    // For now, we'll just log it and ignore if we strictly require a participant
    if (!participant) {
       // In some cases (server sent data), participant might be undefined.
       // We can choose to emit it with a 'system' ID or ignore it.
       // If the app expects a participant SID, we might need to handle this gracefully.
       logger.debug('[LiveKit] data received from system/server or unknown source');
       return;
    }
    
    // Ensure participant is fully connected/recognized before emitting
    this.onMessage(participant.sid, this.textDecoder.decode(message));
  };
}
